import type { ComputedRef, MaybeRef, Ref, UnwrapRef } from 'vue';
import { computed, reactive, ref, unref, watch } from 'vue';
import type { CheckboxChangeEvent } from 'ant-design-vue/es/checkbox/interface';
import type { RadioChangeEvent } from 'ant-design-vue/es/radio/interface';
import type { ColumnType } from 'ant-design-vue/lib/table';
import { debounce } from 'lodash-es';

type Key = number | string;
export type TableOptions = {
  needRowIndex?: boolean | Ref<boolean>;
  checkedList?: Ref<Key[]>;
  checkAll?: boolean;
  tableWidth?: number;
  defaultCheckedList?: MaybeRef<Key[]>;
};

export type DynamicColumnItem = {
  key: Key;
  label: string;
  checked: boolean;
  fixed?: boolean | string;
  width?: number;
  autoHeight?: boolean;
};

export type DynamicColumnState = {
  checkAll: boolean;
  checkedList: Key[];
  needRowIndex: boolean;
  indeterminate: boolean;
  tableWidth: number;
  tableMinWidth: number;
};

export type DynamicColumnConfigState = {
  checkAll: boolean;
  showCheckAction: boolean;
  checkAction: boolean;
  is_column_config: boolean;
};

export interface DynamicColumns {
  dynamicColumns: Ref<ColumnType[]>;
  mergedColumns: Ref<UnwrapRef<ColumnType[]>>;
  dynamicColumnItems: ComputedRef<DynamicColumnItem[]>;
  state: DynamicColumnState;
  configState: DynamicColumnConfigState;
  reset: () => void;
  handleColumnChange: (
    e: CheckboxChangeEvent,
    column: DynamicColumnItem,
    columnChildren: any,
  ) => void;
  handleColumnFixedChange: (e: RadioChangeEvent, column: DynamicColumnItem) => void;
  handleColumnAutoHeightChange: (
    checked: boolean,
    column: DynamicColumnItem,
    columnChildren: any,
  ) => void;
  handleColumnAllClick: (e: CheckboxChangeEvent) => void;
  move: (index: number, targetIndex: number, column: any, children: any) => void;
  change_mode: (mode: any, org_mode: any) => void;
  resizeColumn: (width: number, column: any) => void;
  columnAction: () => void;
  column_change: (column: any) => void;
  replace_column: (tmp_columns: ColumnType[]) => void;
  handleFiltersSorterChange: (filters: any, sorter: any, getFilterKey: any) => void;
}

// columns 支持响应式，排序会影响原数组，所以，不同表格不能也不该使用同一份 columns，如果你需要共用，建议你使用函数返回，确保每一个 table 使用的都是独立的配置
export const useTableDynamicColumns = (
  columns: ColumnType[] | Ref<ColumnType[]>,
  defOptions: TableOptions = {},
  column_list_change?: () => Promise<any>,
): DynamicColumns => {
  const version = ref(1);
  const getKey = (column: ColumnType) =>
    column?.key ??
    (Array.isArray(column.dataIndex)
      ? (column.dataIndex as []).join('$$')
      : (column.dataIndex as string));

  const mergedColumns: Ref<UnwrapRef<ColumnType[]>> = ref<ColumnType[]>([]);

  let defaultCheckedList =
    unref(defOptions.defaultCheckedList) ?? mergedColumns.value.map(column => column.key) ?? [];

  // const checkedList = ref(defaultCheckedList);
  const state = reactive<DynamicColumnState>({
    checkAll: false,
    needRowIndex: false,
    checkedList: defaultCheckedList,
    indeterminate: true,
    tableWidth: defOptions.tableWidth as number,
    tableMinWidth: defOptions.tableWidth as number,
  });

  const change_mode = (mode: any) => {
    if (!mode) {
      return;
    }
    mergedColumns.value.map((item: any, index: number) => {
      item.sort_num = index;
      if (mode[item.key]) {
        if (mode[item.key]['index'] != undefined) {
          item.sort_num = mode[item.key]['index'];
        }
        if (mode[item.key]['checked'] !== false) {
          item.checked = true;
        } else {
          item.checked = false;
        }

        if (mode[item.key]['width'] != undefined) {
          item.width = mode[item.key]['width'];
        }
        if (mode[item.key]['fixed'] != undefined) {
          item.fixed = mode[item.key]['fixed'];
        } else {
          item.fixed = undefined;
        }
        if (mode[item.key]['autoHeight'] != undefined) {
          item.autoHeight = mode[item.key]['autoHeight'];
        } else {
          item.autoHeight = undefined;
        }
        if (mode[item.key]['children'] != undefined) {
          item.children = JSON.parse(JSON.stringify(mode[item.key]['children']));
        }
      }
    });

    mergedColumns.value = mergedColumns.value.sort((a: any, b: any) => a.sort_num - b.sort_num);

    // change_column(0);
    mergedColumns.value.map((column: any) => {
      if (column.checked !== false) {
        if (!state.checkedList.includes(column.key)) {
          // state.checkedList.push(column.key);
          state.checkedList = [...state.checkedList, column.key];
        }
      } else if (state.checkedList.includes(column.key)) {
        state.checkedList = state.checkedList.filter(item => item !== column.key);
      }
    });
  };

  watch(
    () => columns,
    () => {
      mergedColumns.value = unref(columns || [])
        .slice()
        .map(column => {
          return { ...column, key: getKey(column) };
        });
      mergedColumns.value.forEach((column: any) => {
        if (column.checked !== false) {
          if (!state.checkedList.includes(column.key)) {
            // state.checkedList.push(column.key);
            state.checkedList = [...state.checkedList, column.key];
          }
        } else if (state.checkedList.includes(column.key)) {
          state.checkedList = state.checkedList.filter(item => item !== column.key);
        }
      });
    },
    { immediate: true },
  );

  const dynamicColumns = ref(columns);
  const get_children_column = (column: any, list: any, is_children: any) => {
    if (column?.children && column?.children.length > 0) {
      column?.children.map((column1: any) => {
        get_children_column(column1, list, true);
      });
    } else {
      list.push({
        key: getKey(column),
        label: column.title,
        fixed: column.fixed == undefined ? false : column.fixed == true ? 'left' : column.fixed,
        checked: is_children
          ? true
          : defOptions.checkAll
          ? true
          : state.checkedList.includes(column.key),
        width: column.width ? column.width : 150,
        autoHeight: column.autoHeight ? column.autoHeight : false,
      } as DynamicColumnItem);
    }
  };
  const dynamicColumnItems = computed<DynamicColumnItem[]>(() => {
    const list: any = [];
    mergedColumns.value.forEach((column: any) => {
      get_children_column(column, list, false);
    });
    return list;
  });
  // const dynamicColumnValues = computed(() => dynamicColumnItems.value.map(column => column.key));

  // state.checkedList = checkedList.value.filter((key: string) => state.checkedList.includes(key));
  // state.checkAll = defOptions.checkAll as boolean;
  // state.indeterminate = !defOptions.checkAll;

  const planColumns = () => {
    // let newColumns = mergedColumns.value.slice();
    dynamicColumns.value = mergedColumns.value
      .slice()
      .filter((item: any) => {
        return state.checkedList.includes(item.key);
      })
      .map((item: any) => {
        // const ditems = dynamicColumnItems.value.filter(oitem => oitem.key == item.key);
        // if (ditems.length > 0 && ditems[0].width != undefined && item['width'] != undefined) {
        //   item['width'] = ditems[0].width;
        // }
        // item.checked = true;
        const obj: any = {};
        Object.assign(obj, { ...item });
        if (obj.children && obj.children.length > 0) {
          obj.children = obj.children.filter((child: any) => {
            return child.checked !== false;
          });
        }
        return obj;
      });
    // state.tableWidth = sum(
    //   dynamicColumns.value.map(item => (isNumber(item.width) ? item.width : 150)),
    // );
    // if (state.tableWidth < state.tableMinWidth) {
    //   state.tableWidth = state.tableMinWidth;
    // }
    // console.error(dynamicColumns.value);
  };
  watch(
    [() => state.checkedList, mergedColumns],
    () => {
      state.checkAll = state.checkedList.length === mergedColumns.value.length;
      state.indeterminate =
        !!state.checkedList.length && state.checkedList.length < mergedColumns.value.length;
      planColumns();
    },
    { immediate: true, deep: true },
  );

  const handleColumnChange = (
    e: CheckboxChangeEvent,
    column: DynamicColumnItem,
    columnChildren: any,
  ) => {
    const checked = (e.target as HTMLInputElement).checked;
    if (columnChildren) {
      mergedColumns.value = mergedColumns.value.map((item: any) => {
        if (item.key == column.key) {
          item.children.map((item1: any) => {
            if (item1.dataIndex == columnChildren.key) {
              item1.checked = checked;
            }
            return item1;
          });
        }
        return item;
      });
      columnChildren.checked = checked;
    } else {
      mergedColumns.value = mergedColumns.value.map((item: any) => {
        if (item.key == column.key) {
          item.checked = checked;
        }
        return item;
      });
      if (checked) {
        if (!state.checkedList.includes(column.key)) {
          state.checkedList = [...state.checkedList, column.key];
        }
      } else {
        state.checkedList = state.checkedList.filter(item => item !== column.key);
      }
    }
  };

  const handleColumnFixedChange = (e: RadioChangeEvent, column: DynamicColumnItem) => {
    const value = (e.target as HTMLInputElement).value;
    mergedColumns.value = mergedColumns.value.map((item: any) => {
      if (item.key == column.key) {
        item.fixed = value;
      }
      return item;
    });
    column.fixed = value;
  };

  const handleColumnAutoHeightChange = (
    checked: boolean,
    column: DynamicColumnItem,
    columnChildren: any,
  ) => {
    if (columnChildren) {
      mergedColumns.value = mergedColumns.value.map((item: any) => {
        if (item.key == column.key) {
          item.children.map((item1: any) => {
            if (item1.dataIndex == columnChildren.key) {
              item1.autoHeight = checked;
            }
            return item1;
          });
        }
        return item;
      });
      columnChildren.autoHeight = checked;
    } else {
      mergedColumns.value = mergedColumns.value.map((item: any) => {
        if (item.key == column.key) {
          item.autoHeight = checked;
        }
        return item;
      });
      column.autoHeight = checked;
    }
  };

  const handleColumnAllClick = (e: CheckboxChangeEvent) => {
    const checked = (e.target as HTMLInputElement).checked;
    state.checkedList = checked ? dynamicColumnItems.value.map(column => column.key) : [];
  };

  const reset = () => {
    state.checkedList = defaultCheckedList;
    mergedColumns.value = mergedColumns.value
      .sort((a, b) => defaultCheckedList.indexOf(a.key) - defaultCheckedList.indexOf(b.key))
      .slice();
  };

  const move = (index: number, targetIndex: number, column: any, children: any) => {
    if (children) {
      const newData = children.children.slice();
      const dataItem = newData[index];
      newData.splice(index, 1);
      if (targetIndex === 0) {
        newData.unshift(dataItem);
      } else {
        newData.splice(targetIndex, 0, dataItem);
      }
      children.children = newData;
      mergedColumns.value = mergedColumns.value.map((item: any) => {
        if (item.key == children.key) {
          item.children = newData;
        }
        return item;
      });
    } else {
      let mergedIndex = 0;
      let mergedTargetIndex = 0;
      mergedColumns.value.forEach((item: any, index1: any) => {
        if (column[index - 1].key == item.key) {
          mergedIndex = index1;
        }
        if (column[targetIndex - 1].key == item.key) {
          mergedTargetIndex = index1;
        }
      });
      const newColumns = mergedColumns.value.slice();
      const columnItem = newColumns[mergedIndex];
      newColumns.splice(mergedIndex, 1);
      if (mergedTargetIndex === 0) {
        newColumns.unshift(columnItem);
      } else {
        newColumns.splice(mergedTargetIndex, 0, columnItem);
      }
      mergedColumns.value = newColumns;
    }
  };

  /**
   * 更新列的配置，上传到服务器，先判断是否有改变，没有改变则不上传
   */

  const resizeColumn = debounce((width: number, column: any) => {
    mergedColumns.value.map(item => {
      if (item.key == column.key) {
        item.width = width;
      }
    });
  }, 500);

  const columnAction = () => {
    mergedColumns.value.forEach((column: any) => {
      if (column.key == 'action') {
        column.checked = configState.checkAction;
        const b: any = {
          ...column,
          checked: configState.checkAction,
        };
        const a: any = {};
        a.target = b;
        handleColumnChange(a, b, null);
      }
    });
  };

  // 视图的切换 列改变
  const column_change = column => {
    change_mode(column);
  };

  const configState = reactive<DynamicColumnConfigState>({
    checkAction:
      dynamicColumnItems.value.filter(item => item.key == 'action' && item.checked).length > 0,
    showCheckAction: dynamicColumnItems.value.filter(item => item.key == 'action').length > 0,
    checkAll: true,
    is_column_config: false,
  });

  const replace_column = (tmp_columns: ColumnType[]) => {
    unref(columns || []).length = 0;
    tmp_columns.forEach(item => {
      unref(columns || []).push(item);
    });
    // version.value += 1;
    // columns.concat(tmp_columns);
    // init();

    mergedColumns.value = unref(columns || [])
      .slice()
      .map(column => {
        const newColumn = { ...column, key: getKey(column), checked: true };
        if (state.checkedList.length == 0) {
          newColumn.checked = true;
        } else {
          newColumn.checked = state.checkedList.includes(column.key);
        }
        return newColumn;
      });
    // state.checkedList = [];
    mergedColumns.value.forEach((column: any) => {
      if (column.checked !== false) {
        if (!state.checkedList.includes(column.key)) {
          // state.checkedList.push(column.key);
          state.checkedList = [...state.checkedList, column.key];
        }
      } else if (!state.checkedList.includes(column.key)) {
        state.checkedList = state.checkedList.filter(item => item !== column.key);
      }
    });
  };

  const handleFiltersSorterChange = (filters: any, sorter: any, getFilterKey: any) => {
    mergedColumns.value.forEach((item: any) => {
      if (item?.children && item.children.length > 0) {
        item.children.forEach((child: any) => {
          if (
            filters?.[getFilterKey(child.dataIndex)] &&
            filters?.[getFilterKey(child.dataIndex)].length > 0
          ) {
            child.filteredValue = filters[getFilterKey(child.dataIndex)];
          } else {
            child.filteredValue = null;
          }
          if (sorter && sorter.length > 0) {
            if (child?.sorter) {
              child.sortOrder = false;
              sorter.forEach((item1: any) => {
                if (item1.field == child.dataIndex) {
                  child.sortOrder = item1.order;
                }
              });
            }
          } else {
            if (child?.sorter) {
              child.sortOrder = false;
            }
          }
        });
      } else {
        if (filters?.[getFilterKey(item.key)] && filters?.[getFilterKey(item.key)].length > 0) {
          item.filteredValue = filters[getFilterKey(item.key)];
        } else {
          item.filteredValue = null;
        }
        if (sorter && sorter.length > 0) {
          if (item?.sorter) {
            item.sortOrder = false;
            sorter.forEach((item1: any) => {
              if (item1.field == item.key) {
                item.sortOrder = item1.order;
              }
            });
          }
        } else {
          if (item?.sorter) {
            item.sortOrder = false;
          }
        }
      }
    });
  };
  // init();
  return {
    dynamicColumns,
    mergedColumns,
    dynamicColumnItems,
    state,
    configState,
    reset,
    handleColumnAllClick,
    handleColumnChange,
    handleColumnFixedChange,
    handleColumnAutoHeightChange,
    move,
    change_mode,
    resizeColumn,
    columnAction,
    column_change,
    replace_column,
    handleFiltersSorterChange,
  };
};
